import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { toast, Toaster } from "sonner";
import { Loader2, PenLine, Download, CheckCircle2, ShieldCheck } from "lucide-react";
import { Logo } from "@/components/Logo";
import { IOSButton, CARD_SHADOW } from "@/components/ios";
import SignatureSheet from "@/components/legal/SignatureSheet";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SignContract() {
  const { token } = useParams();
  const [doc, setDoc] = useState(null);
  const [error, setError] = useState(null);
  const [signOpen, setSignOpen] = useState(false);
  const [signing, setSigning] = useState(false);
  const [justSigned, setJustSigned] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/sign/${token}`)
      .then((res) => setDoc(res.data))
      .catch((e) => {
        const status = e.response?.status;
        setError(
          status === 410
            ? "This signing link has expired. Ask the sender for a fresh link."
            : "This signing link is not valid. Check the link or ask the sender for a new one."
        );
      });
  }, [token]);

  const sign = async ({ mode, name, signature_b64 }) => {
    setSigning(true);
    try {
      const res = await axios.post(`${API}/sign/${token}/sign`, { mode, name, signature_b64 });
      setJustSigned(res.data);
      setSignOpen(false);
      toast.success("Document signed");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not sign this document");
    } finally {
      setSigning(false);
    }
  };

  const download = async () => {
    try {
      const res = await axios.get(`${API}/sign/${token}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(doc?.title || "document").replace(/[^a-z0-9]+/gi, "-").toLowerCase()}${doc?.signed || justSigned ? "-signed" : ""}.pdf`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch {
      toast.error("Download failed");
    }
  };

  const signed = justSigned || doc?.signed;

  return (
    <div className="min-h-screen bg-[#FDFDFB] text-[#0A0A0A] font-sans" data-testid="sign-page">
      <Toaster position="top-center" richColors />
      <header className="sticky top-0 z-40 bg-white/70 backdrop-blur-2xl backdrop-saturate-150 border-b border-black/[0.05]">
        <div className="max-w-3xl mx-auto px-5 h-16 flex items-center justify-between">
          <Logo size={26} />
          <span className="flex items-center gap-1.5 text-[13px] font-medium text-[#3C3C43]/60">
            <ShieldCheck className="w-4 h-4 text-[#34C759]" strokeWidth={1.5} /> Secure signing
          </span>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-5 py-8 md:py-12 pb-32">
        {error && (
          <div className={`bg-white rounded-sm p-10 text-center ${CARD_SHADOW}`} data-testid="sign-error">
            <p className="text-[17px] font-semibold mb-2">Link unavailable</p>
            <p className="text-[15px] text-[#3C3C43]/60">{error}</p>
          </div>
        )}
        {!doc && !error && (
          <div className="flex justify-center py-24">
            <Loader2 className="w-6 h-6 text-[#3C3C43]/40 animate-spin" strokeWidth={1.5} />
          </div>
        )}

        {doc && (
          <>
            <div className="mb-6">
              <h1 className="text-2xl md:text-3xl font-bold tracking-tight" data-testid="sign-title">{doc.title}</h1>
              <p className="text-[15px] text-[#3C3C43]/60 mt-1">
                {doc.client_name ? `Prepared for ${doc.client_name} · ` : ""}Sent by Doclyx for electronic signature
              </p>
            </div>

            {signed && (
              <div className={`mb-6 bg-white rounded-sm p-6 md:p-8 ${CARD_SHADOW}`} data-testid="sign-success">
                <div className="flex items-start gap-4">
                  <CheckCircle2 className={`w-8 h-8 shrink-0 ${doc.completed ? "text-[#9B703F]" : "text-[#34C759]"}`} strokeWidth={1.5} />
                  <div className="flex-1">
                    <p className="text-[17px] font-semibold">{doc.completed ? "Document completed by both parties" : "Document signed"}</p>
                    <p className="text-[14px] text-[#3C3C43]/60 mt-1">
                      Signed by {justSigned?.signer_name || doc.signer_name} ·{" "}
                      {new Date(justSigned?.signed_at || doc.signed_at).toLocaleString()}.
                      {doc.countersigner_name && ` Countersigned by ${doc.countersigner_name}.`} A timestamped audit trail is
                      embedded in the PDF.
                    </p>
                    <IOSButton variant="plain" data-testid="sign-download-button" onClick={download} className="mt-4">
                      <Download className="w-4 h-4" strokeWidth={1.5} /> Download signed PDF
                    </IOSButton>
                  </div>
                </div>
              </div>
            )}

            <div className={`bg-white rounded-sm overflow-hidden ${CARD_SHADOW}`} data-testid="sign-document">
              {doc.kind === "upload" ? (
                <div className="divide-y divide-black/[0.05] bg-[#FDFDFB] p-3 space-y-3">
                  {(doc.pages || []).map((p, i) => (
                    <div key={i} className="relative">
                      <img
                        src={`data:image/png;base64,${p}`}
                        alt={`Page ${i + 1}`}
                        className="w-full rounded-sm shadow-sm"
                        data-testid={`sign-page-image-${i}`}
                      />
                      {!signed && (doc.fields || []).map((f, fi) =>
                        f.page === i + 1 ? (
                          f.type === "date" ? (
                            <div
                              key={fi}
                              data-testid={`sign-date-field-${fi}`}
                              className="absolute border-2 border-dashed border-[#9B703F] bg-white/90 rounded-sm flex items-center justify-center"
                              style={{ left: `${f.x * 100}%`, top: `${f.y * 100}%`, width: `${f.w * 100}%`, height: `${f.h * 100}%` }}
                            >
                              <span className="text-[10px] font-semibold text-[#9B703F]">Date — filled at signing</span>
                            </div>
                          ) : (
                            <button
                              key={fi}
                              type="button"
                              data-testid={`sign-signature-field-${fi}`}
                              onClick={() => setSignOpen(true)}
                              className="absolute border-2 border-dashed border-[#002B5B] bg-white/90 rounded-sm flex items-center justify-center hover:bg-[#002B5B]/[0.06] active:scale-[0.98] transition-all duration-150"
                              style={{ left: `${f.x * 100}%`, top: `${f.y * 100}%`, width: `${f.w * 100}%`, height: `${f.h * 100}%` }}
                            >
                              <span className="text-[10px] font-semibold text-[#002B5B] flex items-center gap-1">
                                <PenLine className="w-3 h-3" strokeWidth={2} /> Tap to sign
                              </span>
                            </button>
                          )
                        ) : null
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="p-6 md:p-10">
                  <p className="text-[15px] leading-[1.8] whitespace-pre-wrap text-[#0A0A0A]" data-testid="sign-body">
                    {doc.body}
                  </p>
                </div>
              )}
            </div>

            {!signed && (
              <div className="fixed bottom-0 inset-x-0 z-40 bg-white/70 backdrop-blur-2xl backdrop-saturate-150 border-t border-black/[0.05]">
                <div className="max-w-3xl mx-auto px-5 py-4 flex items-center justify-between gap-4">
                  <p className="text-[13px] text-[#3C3C43]/75 hidden sm:block">Review the document, then sign electronically.</p>
                  <IOSButton data-testid="review-sign-button" onClick={() => setSignOpen(true)} className="flex-1 sm:flex-none sm:min-w-[220px]">
                    <PenLine className="w-4 h-4" strokeWidth={1.5} /> Review & sign
                  </IOSButton>
                </div>
              </div>
            )}
          </>
        )}
      </main>

      <SignatureSheet open={signOpen} onClose={() => setSignOpen(false)} onSubmit={sign} busy={signing} />

      <footer className="max-w-3xl mx-auto px-5 pb-10 text-center">
        <Link to="/" className="text-[12px] text-[#3C3C43]/40 hover:text-[#002B5B] transition-colors duration-150">
          Powered by Doclyx
        </Link>
      </footer>
    </div>
  );
}
